export const PENDING_STATUS_TTL_MS = 30_000;

export interface StatusTask {
  id: string;
  status: string;
}

export interface PendingStatusTask<T extends StatusTask> {
  task: T;
  expiresAt: number;
}

/**
 * Discard a local status overlay only once the broad task snapshot reflects it.
 *
 * The broad list is deliberately capped, so an absent row is not evidence that
 * a move was superseded. Callers refresh expired overlays from the authoritative
 * task row before either replacing or removing them.
 */
export function reconcilePendingStatusTasks<T extends StatusTask>(
  pending: Record<string, PendingStatusTask<T>>,
  broadTasks: ReadonlyMap<string, StatusTask>,
  now: number,
): Record<string, PendingStatusTask<T>> {
  let changed = false;
  const next: Record<string, PendingStatusTask<T>> = {};

  Object.entries(pending).forEach(([taskId, entry]) => {
    const broadStatus = broadTasks.get(taskId)?.status;
    if (broadStatus === entry.task.status) {
      changed = true;
      return;
    }
    next[taskId] = entry;
  });

  return changed ? next : pending;
}

/** Return overlays that need an authoritative status refresh before expiry. */
export function expiredPendingStatusTaskIds<T extends StatusTask>(
  pending: Record<string, PendingStatusTask<T>>,
  now: number,
): string[] {
  return Object.entries(pending)
    .filter(([, entry]) => entry.expiresAt <= now)
    .map(([taskId]) => taskId);
}

/**
 * Replace expired local values with the directly-read status. A missing entry
 * in the authoritative response is intentional: it confirms deletion/RLS and
 * removes the task from the dependency lookup instead of guessing "unblocked".
 */
export function refreshExpiredPendingStatusTasks<T extends StatusTask>(
  pending: Record<string, PendingStatusTask<T>>,
  authoritativeStatuses: ReadonlyMap<string, string>,
  now: number,
  nextExpiresAt: number,
): Record<string, PendingStatusTask<T>> {
  let changed = false;
  const next = { ...pending };

  Object.entries(pending).forEach(([taskId, entry]) => {
    if (entry.expiresAt > now) return;
    const status = authoritativeStatuses.get(taskId);
    if (!status) {
      delete next[taskId];
      changed = true;
      return;
    }
    next[taskId] = {
      task: { ...entry.task, status },
      expiresAt: nextExpiresAt,
    };
    changed = true;
  });

  return changed ? next : pending;
}

export function nextPendingStatusExpiry<T extends StatusTask>(
  pending: Record<string, PendingStatusTask<T>>,
): number | null {
  const expiries = Object.values(pending).map((entry) => entry.expiresAt);
  return expiries.length === 0 ? null : Math.min(...expiries);
}

/** Update a cached task list without inventing a row in a filtered result. */
export function mergeTaskIntoCachedList<T extends StatusTask>(
  tasks: T[] | undefined,
  updatedTask: T,
): T[] | undefined {
  if (!tasks) return tasks;

  let found = false;
  const next = tasks.map((task) => {
    if (task.id !== updatedTask.id) return task;
    found = true;
    return { ...task, ...updatedTask };
  });
  return found ? next : tasks;
}

/** A bare mutation result is safe to merge only for status-only task updates. */
export function isStatusOnlyTaskUpdate(updates: object): boolean {
  const fields = Object.keys(updates);
  return fields.length > 0 && fields.every(
    (field) => field === "status" || field === "completed_at",
  );
}
